import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Card, Space, Typography } from 'antd';

const { Title, Text, Paragraph } = Typography;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can also log the error to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      errorInfo
    });
    
    // Call the optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      // Check if a custom fallback was provided
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Default fallback UI
      return (
        <Card style={{ maxWidth: '800px', margin: '50px auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Title level={3} style={{ color: '#cf1322' }}>Something went wrong</Title>
            
            <Alert 
              type="error" 
              message="React Error" 
              description={
                <Text>
                  {this.state.error?.toString() || 'An unexpected error occurred'}
                </Text>
              }
              showIcon
            />
            
            {this.state.error?.message?.includes('Maximum update depth exceeded') && (
              <Alert 
                type="info" 
                message="Infinite Loop Detected" 
                description={
                  <Text>
                    This error is caused by a component repeatedly updating its state, creating an infinite loop. 
                    This often happens when a state update in a useEffect hook causes another update.
                  </Text>
                }
                showIcon
              />
            )}
            
            {this.state.errorInfo && (
              <div style={{ marginTop: '20px' }}>
                <Paragraph strong>Component Stack Trace:</Paragraph>
                <div style={{ 
                  overflow: 'auto', 
                  backgroundColor: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  maxHeight: '300px'
                }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              </div>
            )}
            
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <Button 
                type="primary" 
                onClick={this.resetErrorBoundary}
                style={{ marginRight: '10px' }}
              >
                Try Again
              </Button>
              <Button 
                onClick={() => window.location.href = '/'}
              >
                Go Home
              </Button>
            </div>
          </Space>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 